import TrackedEvent from '@/Enums/Creatives/TrackedEvent.enum';
import type { VastTrackingEvent } from '@/helpers/creatives/vastParser.helpers';
import { Button } from '@mui/material';

interface IProps {
  trackingEvents: VastTrackingEvent[];
  clickThroughUrl: string | null;
  firedEvents: Set<TrackedEvent>;
  remainingSeconds: number | null;
  onClickFired: () => void;
}

const EVENT_LABELS: Record<TrackedEvent, string> = {
  [TrackedEvent.IMPRESSION]: 'Impression',
  [TrackedEvent.PROGRESS]: 'Progress',
  [TrackedEvent.FIRST_QUARTILE]: 'First Quartile (25%)',
  [TrackedEvent.MIDPOINT]: 'Midpoint (50%)',
  [TrackedEvent.THIRD_QUARTILE]: 'Third Quartile (75%)',
  [TrackedEvent.COMPLETE]: 'Complete (100%)',
  [TrackedEvent.CLICK]: 'Click',
  [TrackedEvent.ERROR]: 'Error',
};

const TrackingEventsPanel = ({
  trackingEvents,
  clickThroughUrl,
  firedEvents,
  remainingSeconds,
  onClickFired,
}: IProps) => {
  const hasTrackingForEvent = (event: string) => trackingEvents.some(t => t.event === event);

  const handleVerifyClick = () => {
    onClickFired();
    window.open(clickThroughUrl!, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className='flex flex-col gap-4'>
      <div>
        <p className='font-semibold text-sm mb-3'>Event Tracking</p>
        <div className='flex flex-col gap-2'>
          {Object.values(TrackedEvent).map(event => {
            const fired = firedEvents.has(event);
            const hasTracking = hasTrackingForEvent(event);
            return (
              <div key={event} className='flex items-center gap-2 text-sm'>
                <span
                  className={`w-2.5 h-2.5 rounded-full shrink-0 ${fired ? 'bg-green-500' : 'bg-gray-300'}`}
                  aria-label={`${EVENT_LABELS[event]}: ${fired ? 'fired' : 'not fired'}`}
                />
                <span className={fired ? 'text-green-700 font-medium' : 'text-gray-500'}>
                  {event === TrackedEvent.PROGRESS && fired && remainingSeconds !== null
                    ? `The ad completes in ${remainingSeconds} seconds.`
                    : EVENT_LABELS[event]}
                </span>
                {hasTracking && <span className='text-xs text-gray-400 ml-auto'>tracking pixel</span>}
              </div>
            );
          })}
        </div>
      </div>

      {trackingEvents.length > 0 && (
        <div>
          <p className='font-semibold text-sm mb-2'>Tracking Events ({trackingEvents.length})</p>
          <div className='flex flex-col gap-1 max-h-[150px] overflow-y-auto'>
            {trackingEvents.map((t, i) => (
              <div key={i} className='text-xs text-gray-500 truncate' title={t.url}>
                <span className='font-medium text-gray-700'>{t.event}</span>: {t.url}
              </div>
            ))}
          </div>
        </div>
      )}

      {clickThroughUrl && (
        <div>
          <p className='font-semibold text-sm mb-2'>Click URL</p>
          <p className='text-xs text-gray-500 break-all mb-2'>{clickThroughUrl}</p>
          <Button variant='outlined' size='small' onClick={handleVerifyClick}>
            Verify Click URL
          </Button>
        </div>
      )}
    </div>
  );
};

export default TrackingEventsPanel;
